class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
    this.refreshTimer = null;
    this.subscribeTimer = null;
    this.isSubscribed = false;
    this.handleCartChange = this.handleCartChange.bind(this);
    this.subscribeToCart = this.subscribeToCart.bind(this);
  }

  connectedCallback() {
    this.messageElement = this.querySelector('[data-free-shipping-message]');
    this.progressElement = this.querySelector('[role="progressbar"]');
    this.fillElement = this.querySelector('.cart-free-shipping-bar__fill');

    if (this.dataset.liveCart !== 'true') return;

    this.subscribeToCart();
    this.refresh();
  }

  disconnectedCallback() {
    if (this.isSubscribed && window.eventBus) {
      window.eventBus.off('cart:added', this.handleCartChange);
      window.eventBus.off('update:cart:drawer', this.handleCartChange);
    }
    window.clearTimeout(this.refreshTimer);
    window.clearTimeout(this.subscribeTimer);
    this.isSubscribed = false;
  }

  subscribeToCart() {
    if (this.isSubscribed || !this.isConnected) return;

    if (!window.eventBus) {
      this.subscribeTimer = window.setTimeout(this.subscribeToCart, 50);
      return;
    }

    window.eventBus.on('cart:added', this.handleCartChange);
    window.eventBus.on('update:cart:drawer', this.handleCartChange);
    this.isSubscribed = true;
  }

  handleCartChange() {
    window.clearTimeout(this.refreshTimer);
    this.refreshTimer = window.setTimeout(() => this.refresh(), 80);
  }

  async refresh() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;
      this.update(await response.json());
    } catch (_error) {
      // Keep the server-rendered value if the cart endpoint is unavailable.
    }
  }

  update(cart) {
    const threshold = Number(this.dataset.thresholdCents);
    if (!threshold || !this.messageElement || !this.progressElement || !this.fillElement) return;

    const shippableTotal = (cart.items || []).reduce((total, item) => {
      return item.requires_shipping ? total + item.final_line_price : total;
    }, 0);

    const cartDiscounts = (cart.cart_level_discount_applications || []).reduce((total, discount) => {
      return total + discount.total_allocated_amount;
    }, 0);

    const eligibleTotal = Math.max(0, shippableTotal - cartDiscounts);
    const reached = eligibleTotal >= threshold;
    const progress = Math.min(100, Math.floor((eligibleTotal / threshold) * 100));

    this.classList.toggle('cart-free-shipping-bar--reached', reached);
    this.fillElement.style.width = `${progress}%`;
    this.progressElement.setAttribute('aria-valuenow', String(progress));

    if (reached) {
      this.messageElement.textContent = this.dataset.reachedMessage;
      return;
    }

    const remaining = threshold - eligibleTotal;
    const amount = this.formatMoney(remaining, cart.currency);
    this.messageElement.textContent = this.dataset.remainingTemplate.replace('__AMOUNT__', amount);
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: this.dataset.showCurrencyCode === 'true' ? 'code' : 'symbol',
      }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
